import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import React, { useState, useEffect, Suspense } from 'react';
import loadable from '@loadable/component';
import { useAB } from '@guardian/ab-react';
import { tests } from '@frontend/web/experiments/ab-tests';
import { ShareCount } from '@frontend/web/components/ShareCount';
import { MostViewedFooter } from '@frontend/web/components/MostViewed/MostViewedFooter/MostViewedFooter';
import { ReaderRevenueLinks } from '@frontend/web/components/ReaderRevenueLinks';
import { SlotBodyEnd } from '@root/src/web/components/SlotBodyEnd/SlotBodyEnd';
import { Links } from '@frontend/web/components/Links';
import { SubNav } from '@frontend/web/components/SubNav/SubNav';
import { GetMatchNav } from '@frontend/web/components/GetMatchNav';
import { Discussion } from '@frontend/web/components/Discussion';
import { StickyBottomBanner } from '@root/src/web/components/StickyBottomBanner/StickyBottomBanner';
import { SignInGateSelector } from '@root/src/web/components/SignInGate/SignInGateSelector';
import { incrementWeeklyArticleCount } from '@guardian/automat-client';
import { QandaAtom, GuideAtom, ProfileAtom, TimelineAtom, ChartAtom, PersonalityQuizAtom, KnowledgeQuizAtom, } from '@guardian/atoms-rendering';
import { AudioAtomWrapper } from '@frontend/web/components/AudioAtomWrapper';
import { Portal } from '@frontend/web/components/Portal';
import { HydrateOnce } from '@frontend/web/components/HydrateOnce';
import { Lazy } from '@frontend/web/components/Lazy';
import { Placeholder } from '@root/src/web/components/Placeholder';
import { decideTheme } from '@root/src/web/lib/decideTheme';
import { decideDisplay } from '@root/src/web/lib/decideDisplay';
import { decideDesign } from '@root/src/web/lib/decideDesign';
import { loadScript } from '@root/src/web/lib/loadScript';
import { useOnce } from '@root/src/web/lib/useOnce';
import { initPerf } from '@root/src/web/browser/initPerf';
import { getCookie, addCookie } from '@root/src/web/browser/cookie';
import { getLocaleCode } from '@frontend/web/lib/getCountryCode';
import { getUser } from '@root/src/web/lib/getUser';
import { FocusStyleManager } from '@guardian/src-foundations/utils';
import { incrementAlreadyVisited } from '@root/src/web/lib/alreadyVisited';
import { incrementDailyArticleCount } from '@frontend/web/lib/dailyArticleCount';
import { hasOptedOutOfArticleCount } from '@frontend/web/lib/contributions';
import { buildAdTargeting } from '@root/src/lib/ad-targeting';
import { getSharingUrls } from '@root/src/lib/sharing-urls';
import { cmp, onConsentChange, getConsentFor, } from '@guardian/consent-management-platform';
import { injectPrivacySettingsLink } from '@root/src/web/lib/injectPrivacySettingsLink';
import { updateIframeHeight } from '@root/src/web/browser/updateIframeHeight';
import { ClickToView } from '@root/src/web/components/ClickToView';
import { LabsHeader } from '@root/src/web/components/LabsHeader';
import { submitComponentEvent, } from '../browser/ophan/ophan';
import { trackPerformance } from '../browser/ga/ga';
import { decidePalette } from '../lib/decidePalette';
import { buildBrazeMessages } from '../lib/braze/buildBrazeMessages';
import { CommercialMetrics } from './CommercialMetrics';
// *******************************
// ****** Dynamic imports ********
// *******************************
const EditionDropdown = loadable(() => import('@frontend/web/components/EditionDropdown'), {
    resolveComponent: (module) => module.EditionDropdown,
});
const MostViewedRightWrapper = React.lazy(() => {
    const { start, end } = initPerf('MostViewedRightWrapper');
    start();
    return import(
    /* webpackChunkName: "MostViewedRightWrapper" */ '@frontend/web/components/MostViewed/MostViewedRight/MostViewedRightWrapper').then((module) => {
        end();
        return { default: module.MostViewedRightWrapper };
    });
});
const OnwardsUpper = React.lazy(() => {
    const { start, end } = initPerf('OnwardsUpper');
    start();
    return import(
    /* webpackChunkName: "OnwardsUpper" */ '@frontend/web/components/Onwards/OnwardsUpper').then((module) => {
        end();
        return { default: module.OnwardsUpper };
    });
});
const OnwardsLower = React.lazy(() => {
    const { start, end } = initPerf('OnwardsLower');
    start();
    return import(
    /* webpackChunkName: "OnwardsLower" */ '@frontend/web/components/Onwards/OnwardsLower').then((module) => {
        end();
        return { default: module.OnwardsLower };
    });
});
const GetMatchStats = React.lazy(() => {
    const { start, end } = initPerf('GetMatchStats');
    start();
    return import(
    /* webpackChunkName: "GetMatchStats" */ '@frontend/web/components/GetMatchStats').then((module) => {
        end();
        return { default: module.GetMatchStats };
    });
});
export const App = ({ CAPI, NAV, ophanRecord }) => {
    const [isSignedIn, setIsSignedIn] = useState();
    const [user, setUser] = useState();
    const [countryCode, setCountryCode] = useState();
    // This is an async version of the countryCode state value defined above.
    // This can be used where you've got logic which depends on countryCode but
    // don't want to block on it becoming available, as you would with the
    // non-async version (this is the case in the banner picker where some
    // banners need countryCode but we don't want to block all banners from
    // executing their canShow logic until countryCode is available):
    const [asyncCountryCode, setAsyncCountryCode] = useState();
    const [brazeMessages, setBrazeMessages] = useState();
    const pageViewId = window.guardian?.config?.ophan?.pageViewId;
    const [browserId, setBrowserId] = useState(undefined);
    useOnce(() => {
        // TODO: can the browserId actually be null?
        setBrowserId(getCookie('bwid') ?? undefined);
    }, []);
    const componentEventHandler = (componentType, id, action) => () => {
        const componentEvent = {
            component: {
                componentType,
                id,
                products: [],
                labels: [],
            },
            action,
        };
        submitComponentEvent(componentEvent, ophanRecord);
    };
    // *******************************
    // ** Setup AB Test Tracking *****
    // *******************************
    const ABTestAPI = useAB();
    useEffect(() => {
        const allRunnableTests = ABTestAPI.allRunnableTests(tests);
        ABTestAPI.trackABTests(allRunnableTests);
        ABTestAPI.registerImpressionEvents(allRunnableTests);
        ABTestAPI.registerCompleteEvents(allRunnableTests);
    }, [ABTestAPI]);
    useEffect(() => {
        setIsSignedIn(!!getCookie('GU_U'));
    }, []);
    useOnce(() => {
        // useOnce means this code will only run once isSignedIn is defined, and only
        // run one time
        if (isSignedIn) {
            getUser(CAPI.config.discussionApiUrl)
                .then((theUser) => {
                if (theUser)
                    setUser(theUser);
            })
                .catch((e) => console.error(`getUser - error: ${e}`));
        }
        else {
            setUser(null);
        }
    }, [isSignedIn, CAPI.config.discussionApiUrl]);
    useEffect(() => {
        const callFetch = () => {
            const countryCodePromise = getLocaleCode();
            setAsyncCountryCode(countryCodePromise);
            countryCodePromise
                .then((cc) => setCountryCode(cc || ''))
                .catch((e) => console.error(`countryCodePromise - error: ${e}`));
        };
        callFetch();
    }, []);
    useEffect(() => {
        incrementAlreadyVisited();
    }, []);
    // Log an article view using the Slot Machine client lib
    // This function must be called once per article serving.
    // We should monitor this function call to ensure it only happens within an
    // article pages when other pages are supported by DCR.
    useEffect(() => {
        const incrementArticleCountsIfConsented = async () => {
            const hasOptedOut = await hasOptedOutOfArticleCount();
            if (!hasOptedOut) {
                incrementDailyArticleCount();
                incrementWeeklyArticleCount();
            }
        };
        incrementArticleCountsIfConsented().catch((e) => console.error(`incrementArticleCountsIfConsented - error: ${e}`));
    }, []);
    // AnniversaryAtom
    // Add a cookie for the serverside A/B test that is checked to see if we should
    // show the anniversary atom. This means that this user will not see the atom
    // on the next and following page views.
    useEffect(() => {
        if (CAPI.config.switches.anniversaryArticleHeader) {
            addCookie('X-GU-Experiment-0perc-D', 'true', 2); // 2 days to live means that the atom will show when the switch is on Wednesday AND Saturday
        }
    });
    // Ensure the focus state of any buttons/inputs in any of the Source
    // components are only applied when navigating via keyboard.
    // READ: https://www.theguardian.design/2a1e5182b/p/6691bb-accessibility/t/32e9fb
    useEffect(() => {
        FocusStyleManager.onlyShowFocusOnTabs();
    }, []);
    useEffect(() => {
        // Used internally only, so only import each function on demand
        const loadAndRun = (key) => (asExistingSupporter) => import(
        /* webpackChunkName: "readerRevenueDevUtils" */ '@frontend/web/lib/readerRevenueDevUtils')
            .then((utils) => utils[key](asExistingSupporter, CAPI.shouldHideReaderRevenue))
            /* eslint-disable no-console */
            .catch((error) => console.log('Error loading readerRevenueDevUtils', error));
        /* eslint-enable no-console */
        if (window && window.guardian) {
            window.guardian.readerRevenue = {
                changeGeolocation: loadAndRun('changeGeolocation'),
                showMeTheEpic: loadAndRun('showMeTheEpic'),
                showMeTheBanner: loadAndRun('showMeTheBanner'),
                showNextVariant: loadAndRun('showNextVariant'),
                showPreviousVariant: loadAndRun('showPreviousVariant'),
            };
        }
    }, [CAPI.shouldHideReaderRevenue]);
    // kick off the CMP...
    useEffect(() => {
        // the UI is injected automatically into the page,
        // and is not a react component, so it's
        // handled in here.
        if (CAPI.config.switches.consentManagement && countryCode) {
            const pubData = {
                platform: 'next-gen',
                browserId,
                pageViewId,
            };
            injectPrivacySettingsLink(); // manually updates the footer DOM because it's not hydrated
            // keep this in sync with CONSENT_TIMING in static/src/javascripts/boot.js in frontend
            // mark: CONSENT_TIMING
            let recordedConsentTime = false;
            onConsentChange(() => {
                if (!recordedConsentTime) {
                    recordedConsentTime = true;
                    cmp.willShowPrivacyMessage()
                        .then((willShow) => {
                        trackPerformance('consent', 'acquired', willShow ? 'new' : 'existing');
                    })
                        .catch((e) => console.error(`CMP willShowPrivacyMessage - error: ${e}`));
                }
            });
            cmp.init({
                country: countryCode,
                pubData,
            });
        }
    }, [
        countryCode,
        CAPI.config.switches.consentManagement,
        pageViewId,
        browserId,
    ]);
    // ************************
    // *   Google Analytics   *
    // ************************
    useEffect(() => {
        onConsentChange((state) => {
            const consentGiven = getConsentFor('google-analytics', state);
            if (consentGiven) {
                Promise.all([
                    loadScript('https://www.google-analytics.com/analytics.js'),
                    loadScript(window.guardian.gaPath),
                ]).catch((e) => console.error(`GA - error: ${e}`));
            }
            else {
                // We should never be able to directly set things to the global window object.
                // But in this case we want to stub things for testing, so it's ok to ignore this rule
                // @ts-ignore
                window.ga = null;
            }
        });
    }, []);
    const display = decideDisplay(CAPI.format);
    const design = decideDesign(CAPI.format);
    const pillar = decideTheme(CAPI.format);
    useOnce(() => {
        setBrazeMessages(buildBrazeMessages(isSignedIn, CAPI.config.idApiUrl));
    }, [isSignedIn, CAPI.config.idApiUrl]);
    const format = {
        display,
        design,
        theme: pillar,
    };
    const palette = decidePalette(format);
    const adTargeting = buildAdTargeting(CAPI.config);
    // There are docs on loadable in ./docs/loadable-components.md
    const YoutubeBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.YoutubeBlockElement').length > 0) {
            return import('@frontend/web/components/elements/YoutubeBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.YoutubeBlockComponent,
    });
    const RichLinkComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.RichLinkBlockElement').length > 0) {
            return import('@frontend/web/components/elements/RichLinkComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.RichLinkComponent,
    });
    const InteractiveBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.InteractiveBlockElement').length > 0) {
            return import('@frontend/web/components/elements/InteractiveBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.InteractiveBlockComponent,
    });
    const InteractiveContentsBlockElement = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.InteractiveContentsBlockElement').length > 0) {
            return import('@frontend/web/components/elements/InteractiveContentsBlockElement');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.InteractiveContentsBlockElement,
    });
    const CalloutBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.CalloutBlockElement').length > 0) {
            return import('@frontend/web/components/elements/CalloutBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.CalloutBlockComponent,
    });
    const DocumentBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.DocumentBlockElement').length > 0) {
            return import('@frontend/web/components/elements/DocumentBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.DocumentBlockComponent,
    });
    const EmbedBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.EmbedBlockElement').length > 0) {
            return import('@frontend/web/components/elements/EmbedBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.EmbedBlockComponent,
    });
    const UnsafeEmbedBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.EmbedBlockElement').length > 0) {
            return import('@frontend/web/components/elements/UnsafeEmbedBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.UnsafeEmbedBlockComponent,
    });
    const InstagramBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.InstagramBlockElement').length > 0) {
            return import('@frontend/web/components/elements/InstagramBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.InstagramBlockComponent,
    });
    const MapEmbedBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.MapBlockElement').length > 0) {
            return import('@frontend/web/components/elements/MapEmbedBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.MapEmbedBlockComponent,
    });
    const SpotifyBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.SpotifyBlockElement').length > 0) {
            return import('@frontend/web/components/elements/SpotifyBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.SpotifyBlockComponent,
    });
    const VideoFacebookBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.VideoFacebookBlockElement').length > 0) {
            return import('@frontend/web/components/elements/VideoFacebookBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.VideoFacebookBlockComponent,
    });
    const VineBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.VineBlockElement').length > 0) {
            return import('@frontend/web/components/elements/VineBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.VineBlockComponent,
    });
    // We use this function to filter the elementsToHydrate array by a particular
    // type so that we can hydrate them. We use T to force the type and keep TS
    // content because *we* know that if _type equals a thing then the type is
    // guaranteed but TS isn't so sure and needs assurance
    const elementsByType = (elements, type) => elements.filter((element) => element._type === type);
    const youTubeAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.YoutubeBlockElement');
    const quizAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.QuizAtomBlockElement');
    const callouts = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.CalloutBlockElement');
    const chartAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.ChartAtomBlockElement');
    const audioAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.AudioAtomBlockElement');
    const qandaAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.QABlockElement');
    const guideAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.GuideAtomBlockElement');
    const profileAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.ProfileAtomBlockElement');
    const timelineAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.TimelineBlockElement');
    const documents = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.DocumentBlockElement');
    const embeds = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.EmbedBlockElement');
    const instas = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.InstagramBlockElement');
    const maps = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.MapBlockElement');
    const spotifies = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.SpotifyBlockElement');
    const facebookVideos = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.VideoFacebookBlockElement');
    const vines = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.VineBlockElement');
    const richLinks = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.RichLinkBlockElement');
    const interactiveElements = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.InteractiveBlockElement');
    const interactiveContentsElement = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.InteractiveContentsBlockElement');
    return (
    // Do you need to HydrateOnce or do you want a Portal?
    //
    // HydrateOnce: If your component is server rendered and you're hydrating it with
    // more data or making it interactive on the client and you do not need to access
    // global application state.
    //
    // Portal: If your component is not server rendered but a pure client-side component
    // and/or you want to access global application state, you want to use a Portal.
    //
    // Note: Both require a 'root' element that needs to be server rendered.
    _jsxs(React.StrictMode, { children: [[
                CAPI.config.switches.commercialMetrics,
                window.guardian.config?.ophan !== undefined,
            ].every(Boolean) && (_jsx(CommercialMetrics, { browserId: browserId, pageViewId: pageViewId }, void 0)),
            _jsx(Portal, Object.assign({ rootId: "reader-revenue-links-header" }, { children: _jsx(ReaderRevenueLinks, { urls: CAPI.nav.readerRevenueLinks.header, edition: CAPI.editionId, countryCode: countryCode, dataLinkNamePrefix: "nav2 : ", inHeader: true, remoteHeaderEnabled: CAPI.config.remoteHeader, pageViewId: pageViewId, contributionsServiceUrl: CAPI.contributionsServiceUrl, ophanRecord: ophanRecord }, void 0) }), void 0),
            _jsx(HydrateOnce, Object.assign({ rootId: "links-root", waitFor: [user] }, { children: _jsx(Links, { supporterCTA: CAPI.nav.readerRevenueLinks.header.supporter, userId: user ? user.userId : undefined, idUrl: CAPI.config.idUrl, mmaUrl: CAPI.config.mmaUrl }, void 0) }), void 0),
            _jsx(HydrateOnce, Object.assign({ rootId: "edition-root" }, { children: _jsx(EditionDropdown, { edition: CAPI.editionId, dataLinkName: "nav2 : topbar : edition-picker: toggle" }, void 0) }), void 0),
            _jsx(HydrateOnce, Object.assign({ rootId: "labs-header" }, { children: _jsx(LabsHeader, {}, void 0) }), void 0),
            CAPI.config.switches.serverShareCounts && (_jsx(Portal, Object.assign({ rootId: "share-count-root" }, { children: _jsx(ShareCount, { ajaxUrl: CAPI.config.ajaxUrl, pageId: CAPI.pageId }, void 0) }), void 0)),
            youTubeAtoms.map((youTubeAtom) => (_jsx(HydrateOnce, Object.assign({ rootId: youTubeAtom.elementId }, { children: _jsx(YoutubeBlockComponent, { format: format, palette: palette, hideCaption: false, 
                    // eslint-disable-next-line jsx-a11y/aria-role
                    role: "inline", adTargeting: adTargeting, isMainMedia: false, id: youTubeAtom.id, assetId: youTubeAtom.assetId, expired: youTubeAtom.expired, overrideImage: youTubeAtom.overrideImage, posterImage: youTubeAtom.posterImage, duration: youTubeAtom.duration, mediaTitle: youTubeAtom.mediaTitle, altText: youTubeAtom.altText }, void 0) }), void 0))),
            interactiveElements.map((interactiveBlock) => (_jsx(HydrateOnce, Object.assign({ rootId: interactiveBlock.elementId }, { children: _jsx(InteractiveBlockComponent, { url: interactiveBlock.url, scriptUrl: interactiveBlock.scriptUrl, alt: interactiveBlock.alt, role: interactiveBlock.role, caption: interactiveBlock.caption, format: format, palette: palette }, void 0) }), void 0))),
            interactiveContentsElement.map((interactiveBlock) => (_jsx(HydrateOnce, Object.assign({ rootId: interactiveBlock.elementId }, { children: _jsx(InteractiveContentsBlockElement, { subheadingLinks: interactiveBlock.subheadingLinks, endDocumentElementId: interactiveBlock.endDocumentElementId }, void 0) }), void 0))),
            quizAtoms.map((quizAtom) => (_jsx(HydrateOnce, Object.assign({ rootId: quizAtom.elementId }, { children: _jsxs(_Fragment, { children: [quizAtom.quizType === 'personality' && (_jsx(PersonalityQuizAtom, { id: quizAtom.id, questions: quizAtom.questions, resultBuckets: quizAtom.resultBuckets, sharingUrls: getSharingUrls(CAPI.pageId, CAPI.webTitle), theme: format.theme }, void 0)),
                        quizAtom.quizType === 'knowledge' && (_jsx(KnowledgeQuizAtom, { id: quizAtom.id, questions: quizAtom.questions, resultGroups: quizAtom.resultGroups, sharingUrls: getSharingUrls(CAPI.pageId, CAPI.webTitle), theme: format.theme }, void 0))] }, void 0) }), void 0))),
            NAV.subNavSections && (_jsx(HydrateOnce, Object.assign({ rootId: "sub-nav-root" }, { children: _jsx(_Fragment, { children: _jsx(SubNav, { subNavSections: NAV.subNavSections, currentNavLink: NAV.currentNavLink, palette: palette, format: format }, void 0) }, void 0) }), void 0)),
            CAPI.matchUrl && (_jsx(Portal, Object.assign({ rootId: "match-nav" }, { children: _jsx(GetMatchNav, { matchUrl: CAPI.matchUrl }, void 0) }), void 0)),
            richLinks.map((richLink, index) => (_jsx(Portal, Object.assign({ rootId: richLink.elementId }, { children: _jsx(RichLinkComponent, { element: richLink, ajaxEndpoint: CAPI.config.ajaxUrl, richLinkIndex: index }, void 0) }), void 0))),
            callouts.map((callout) => (_jsx(HydrateOnce, Object.assign({ rootId: callout.elementId }, { children: _jsx(CalloutBlockComponent, { callout: callout, palette: palette }, void 0) }), void 0))),
            chartAtoms.map((chartAtom) => (_jsx(HydrateOnce, Object.assign({ rootId: chartAtom.elementId }, { children: _jsx(ChartAtom, { id: chartAtom.id, html: chartAtom.html }, void 0) }), void 0))),
            audioAtoms.map((audioAtom) => (_jsx(HydrateOnce, Object.assign({ rootId: audioAtom.elementId }, { children: _jsx(AudioAtomWrapper, { id: audioAtom.id, trackUrl: audioAtom.trackUrl, kicker: audioAtom.kicker, title: audioAtom.title, duration: audioAtom.duration, pillar: pillar, contentIsNotSensitive: !CAPI.config.isSensitive, aCastisEnabled: CAPI.config.switches.acast, readerCanBeShownAds: !CAPI.isAdFreeUser }, void 0) }), void 0))),
            qandaAtoms.map((qandaAtom) => (_jsx(HydrateOnce, Object.assign({ rootId: qandaAtom.elementId }, { children: _jsx(QandaAtom, { id: qandaAtom.id, title: qandaAtom.title, html: qandaAtom.html, image: qandaAtom.img, credit: qandaAtom.credit, pillar: pillar, likeHandler: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'EXPAND') }, void 0) }), void 0))),
            guideAtoms.map((guideAtom) => (_jsx(HydrateOnce, Object.assign({ rootId: guideAtom.elementId }, { children: _jsx(GuideAtom, { id: guideAtom.id, title: guideAtom.title, html: guideAtom.html, image: guideAtom.img, credit: guideAtom.credit, pillar: pillar, likeHandler: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'EXPAND') }, void 0) }), void 0))),
            profileAtoms.map((profileAtom) => (_jsx(HydrateOnce, Object.assign({ rootId: profileAtom.elementId }, { children: _jsx(ProfileAtom, { id: profileAtom.id, title: profileAtom.title, html: profileAtom.html, image: profileAtom.img, credit: profileAtom.credit, pillar: pillar, likeHandler: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'EXPAND') }, void 0) }), void 0))),
            timelineAtoms.map((timelineAtom) => (_jsx(HydrateOnce, Object.assign({ rootId: timelineAtom.elementId }, { children: _jsx(TimelineAtom, { id: timelineAtom.id, title: timelineAtom.title, events: timelineAtom.events, description: timelineAtom.description, pillar: pillar, likeHandler: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'EXPAND') }, void 0) }), void 0))),
            documents.map((document) => (_jsx(HydrateOnce, Object.assign({ rootId: document.elementId }, { children: _jsx(ClickToView, Object.assign({ role: document.role, isTracking: document.isThirdPartyTracking, source: document.source, sourceDomain: document.sourceDomain }, { children: _jsx(DocumentBlockComponent, { embedUrl: document.embedUrl, height: document.height, width: document.width, title: document.title, source: document.source }, void 0) }), void 0) }), void 0))),
            embeds.map((embed, index) => (_jsx(HydrateOnce, Object.assign({ rootId: embed.elementId }, { children: embed.safe ? (_jsx(ClickToView, Object.assign({ role: embed.role, isTracking: embed.isThirdPartyTracking, source: embed.source, sourceDomain: embed.sourceDomain }, { children: _jsx(EmbedBlockComponent, { html: embed.html, alt: embed.alt }, void 0) }), void 0)) : (_jsx(ClickToView, Object.assign({ role: embed.role, isTracking: embed.isThirdPartyTracking, source: embed.source, sourceDomain: embed.sourceDomain, onAccept: () => updateIframeHeight(`iframe[name="unsafe-embed-${index}"]`) }, { children: _jsx(UnsafeEmbedBlockComponent, { html: embed.html, alt: embed.alt || '', index: index }, embed.elementId) }), void 0)) }), void 0))),
            instas.map((insta, index) => (_jsx(HydrateOnce, Object.assign({ rootId: insta.elementId }, { children: _jsx(ClickToView, Object.assign({ role: insta.role, isTracking: insta.isThirdPartyTracking, source: insta.source, sourceDomain: insta.sourceDomain, onAccept: () => updateIframeHeight(`iframe[name="instagram-embed-${index}"]`) }, { children: _jsx(InstagramBlockComponent, { element: insta, index: index }, void 0) }), void 0) }), void 0))),
            maps.map((map) => (_jsx(HydrateOnce, Object.assign({ rootId: map.elementId }, { children: _jsx(ClickToView, Object.assign({ role: map.role, isTracking: map.isThirdPartyTracking, source: map.source, sourceDomain: map.sourceDomain }, { children: _jsx(MapEmbedBlockComponent, { format: format, palette: palette, embedUrl: map.embedUrl, height: map.height, width: map.width, caption: map.caption, credit: map.source, title: map.title }, void 0) }), void 0) }), void 0))),
            spotifies.map((spotify) => (_jsx(HydrateOnce, Object.assign({ rootId: spotify.elementId }, { children: _jsx(ClickToView, Object.assign({ role: spotify.role, isTracking: spotify.isThirdPartyTracking, source: spotify.source, sourceDomain: spotify.sourceDomain }, { children: _jsx(SpotifyBlockComponent, { embedUrl: spotify.embedUrl, height: spotify.height, width: spotify.width, title: spotify.title, format: format, palette: palette, caption: spotify.caption, credit: "Spotify" }, void 0) }), void 0) }), void 0))),
            facebookVideos.map((facebookVideo) => (_jsx(HydrateOnce, Object.assign({ rootId: facebookVideo.elementId }, { children: _jsx(ClickToView, Object.assign({ role: facebookVideo.role, isTracking: facebookVideo.isThirdPartyTracking, source: facebookVideo.source, sourceDomain: facebookVideo.sourceDomain }, { children: _jsx(VideoFacebookBlockComponent, { format: format, palette: palette, embedUrl: facebookVideo.embedUrl, height: facebookVideo.height, width: facebookVideo.width, caption: facebookVideo.caption, credit: facebookVideo.caption, title: facebookVideo.caption }, void 0) }), void 0) }), void 0))),
            vines.map((vine) => (_jsx(HydrateOnce, Object.assign({ rootId: vine.elementId }, { children: _jsx(ClickToView
                // No role given by CAPI
                // eslint-disable-next-line jsx-a11y/aria-role
                , Object.assign({ 
                    // No role given by CAPI
                    // eslint-disable-next-line jsx-a11y/aria-role
                    role: "inline", isTracking: vine.isThirdPartyTracking, source: vine.source, sourceDomain: vine.sourceDomain }, { children: _jsx(VineBlockComponent, { element: vine }, void 0) }), void 0) }), void 0))),
            _jsx(Portal, Object.assign({ rootId: "most-viewed-right" }, { children: _jsx(Lazy, Object.assign({ margin: 100 }, { children: _jsx(Suspense, Object.assign({ fallback: _jsx(_Fragment, {}, void 0) }, { children: _jsx(MostViewedRightWrapper, {}, void 0) }), void 0) }), void 0) }), void 0),
            CAPI.matchUrl && (_jsx(Portal, Object.assign({ rootId: "match-stats" }, { children: _jsx(Lazy, Object.assign({ margin: 300 }, { children: _jsx(Suspense, Object.assign({ fallback: _jsx(Placeholder, { height: 800 }, void 0) }, { children: _jsx(GetMatchStats, { matchUrl: CAPI.matchUrl }, void 0) }), void 0) }), void 0) }), void 0)),
            _jsx(Portal, Object.assign({ rootId: "slot-body-end" }, { children: _jsx(SlotBodyEnd, { isSignedIn: isSignedIn, countryCode: countryCode, contentType: CAPI.contentType, sectionName: CAPI.sectionName, shouldHideReaderRevenue: CAPI.shouldHideReaderRevenue, isMinuteArticle: CAPI.pageType.isMinuteArticle, isPaidContent: CAPI.pageType.isPaidContent, tags: CAPI.tags, contributionsServiceUrl: CAPI.contributionsServiceUrl, brazeMessages: brazeMessages, idApiUrl: CAPI.config.idApiUrl, stage: CAPI.stage }, void 0) }), void 0),
            _jsx(Portal, Object.assign({ rootId: isSignedIn
                    ? 'onwards-upper-whensignedin'
                    : 'onwards-upper-whensignedout' }, { children: _jsx(Lazy, Object.assign({ margin: 300 }, { children: _jsx(Suspense, Object.assign({ fallback: _jsx(_Fragment, {}, void 0) }, { children: _jsx(OnwardsUpper, { ajaxUrl: CAPI.config.ajaxUrl, hasRelated: CAPI.hasRelated, hasStoryPackage: CAPI.hasStoryPackage, isAdFreeUser: CAPI.isAdFreeUser, pageId: CAPI.pageId, isPaidContent: CAPI.config.isPaidContent || false, showRelatedContent: CAPI.config.showRelatedContent, keywordIds: CAPI.config.keywordIds, contentType: CAPI.contentType, tags: CAPI.tags, format: format }, void 0) }), void 0) }), void 0) }), void 0),
            _jsx(Portal, Object.assign({ rootId: isSignedIn
                    ? 'onwards-lower-whensignedin'
                    : 'onwards-lower-whensignedout' }, { children: _jsx(Lazy, Object.assign({ margin: 300 }, { children: _jsx(Suspense, Object.assign({ fallback: _jsx(_Fragment, {}, void 0) }, { children: _jsx(OnwardsLower, { ajaxUrl: CAPI.config.ajaxUrl, hasStoryPackage: CAPI.hasStoryPackage, tags: CAPI.tags, format: format }, void 0) }), void 0) }), void 0) }), void 0),
            _jsx(Portal, Object.assign({ rootId: "sign-in-gate" }, { children: _jsx(SignInGateSelector, { isSignedIn: isSignedIn, CAPI: CAPI }, void 0) }), void 0),
            _jsx(HydrateOnce, Object.assign({ rootId: "comments", waitFor: [user] }, { children: _jsx(Discussion, { discussionApiUrl: CAPI.config.discussionApiUrl, shortUrlId: CAPI.config.shortUrlId, isCommentable: CAPI.isCommentable, pillar: pillar, palette: palette, user: user || undefined, discussionD2Uid: CAPI.config.discussionD2Uid, discussionApiClientHeader: CAPI.config.discussionApiClientHeader, enableDiscussionSwitch: CAPI.config.enableDiscussionSwitch, isAdFreeUser: CAPI.isAdFreeUser, shouldHideAds: CAPI.shouldHideAds, beingHydrated: true, display: display }, void 0) }), void 0),
            _jsx(Portal, Object.assign({ rootId: "most-viewed-footer" }, { children: _jsx(MostViewedFooter, { palette: palette, sectionName: CAPI.sectionName, ajaxUrl: CAPI.config.ajaxUrl, display: display }, void 0) }), void 0),
            _jsx(Portal, Object.assign({ rootId: "reader-revenue-links-footer" }, { children: _jsx(Lazy, Object.assign({ margin: 300 }, { children: _jsx(ReaderRevenueLinks, { urls: CAPI.nav.readerRevenueLinks.footer, edition: CAPI.editionId, countryCode: countryCode, dataLinkNamePrefix: "footer : ", inHeader: false, remoteHeaderEnabled: false, pageViewId: pageViewId, contributionsServiceUrl: CAPI.contributionsServiceUrl, ophanRecord: ophanRecord }, void 0) }), void 0) }), void 0),
            _jsx(Portal, Object.assign({ rootId: "bottom-banner" }, { children: _jsx(StickyBottomBanner, { isSignedIn: isSignedIn, asyncCountryCode: asyncCountryCode, CAPI: CAPI, brazeMessages: brazeMessages, isPreview: !!CAPI.isPreview }, void 0) }), void 0)] }, void 0));
};
